import clsx from 'clsx'
import type {
  AriaAttributes,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
} from 'react'
import React from 'react'
import type SvgIcon from '../SvgIcon/SvgIcon'
import Group, { useGroup } from './Group'

interface TabProps {
  'aria-controls'?: AriaAttributes['aria-controls']
  children?: ReactNode & { type?: { uiName?: string } }
  color?: TabColor
  disabled?: boolean
  id?: string
  onClick?: MouseEventHandler<HTMLButtonElement>
  size?: TabSize
  startAdornment?: typeof SvgIcon
  value: string
}

export type TabColor = 'default' | 'primary' | 'secondary'

export type TabSize = 'large' | 'medium' | 'small'

type TabSizes = {
  [key in TabSize]: {
    root: string
    icon: string
  }
}

type TabVariants = {
  [key in TabColor]: {
    selected: string
    unselected: string
  }
}

const sizes: TabSizes = {
  large: {
    root: 'p-4',
    icon: 'w-6 h-6 pl-1',
  },
  medium: {
    root: 'p-2',
    icon: 'w-6 h-6 pl-1',
  },
  small: {
    root: 'p-1',
    icon: 'w-6 h-6 pl-1',
  },
}

const variants: TabVariants = {
  default: {
    selected: `border-b-2 border-solid border-black-90 text-black-90 active:bg-black-20 focus:bg-black-10 focus-visible:ring hover:bg-black-10`,
    unselected: `border-b-2 border-solid border-transparent text-black-90 active:bg-black-20 focus:bg-black-5 focus-visible:ring hover:bg-black-5`,
  },
  primary: {
    selected: `border-b-2 border-solid border-primary-main text-primary-main active:bg-primary-20 focus:bg-primary-10 focus-visible:ring hover:bg-primary-10`,
    unselected: `border-b-2 border-solid border-transparent text-primary-main active:bg-primary-20 focus:bg-primary-10 focus-visible:ring hover:bg-primary-10`,
  },
  secondary: {
    selected: `border-b-2 border-solid border-secondary-main text-secondary-main active:bg-secondary-20 focus:bg-secondary-10 focus-visible:ring hover:bg-secondary-10`,
    unselected: `border-b-2 border-solid border-transparent text-secondary-main active:bg-secondary-20 focus:bg-secondary-10 focus-visible:ring hover:bg-secondary-10`,
  },
}

function Tab({
  'aria-controls': controls,
  children,
  color,
  disabled,
  id,
  onClick,
  size,
  startAdornment: StartAdornment,
  value,
}: PropsWithChildren<TabProps>): JSX.Element {
  const {
    color: groupColor,
    disabled: groupDisabled,
    onClick: groupClick,
    selectedTab,
    setSelectedTab,
    size: groupSize,
    adornmentPosition,
  } = useGroup()

  const isDisabled = disabled ?? groupDisabled

  const isSelected = selectedTab === value

  const handleSelectTab: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (!isDisabled) {
      setSelectedTab(value)
      onClick ? onClick(event) : groupClick?.(event)
    }
  }

  return (
    <button
      aria-controls={controls}
      aria-selected={isSelected}
      className={clsx(
        'flex-grow min-w-fit font-sans text-sm font-medium leading-relaxed tracking-wide disabled:cursor-not-allowed focus:outline-none disabled:border-black-25 disabled:bg-transparent disabled:text-black-25',
        isSelected
          ? variants[color ?? groupColor].selected
          : variants[color ?? groupColor].unselected,
        sizes[size ?? groupSize].root,
      )}
      disabled={isDisabled}
      id={id}
      onClick={handleSelectTab}
      role="tab"
      value={value}
    >
      <div
        className={clsx(
          'flex justify-center items-center',
          adornmentPosition === 'vertical' ? 'flex-col' : '',
        )}
      >
        {StartAdornment ? (
          <span
            className={clsx(
              'flex items-center justify-center',
              sizes[size ?? groupSize].icon,
            )}
          >
            <StartAdornment color="inherit" />
          </span>
        ) : null}

        {children?.type?.uiName === 'SvgIcon' ? (
          <span className={sizes[size ?? groupSize].icon}>{children}</span>
        ) : (
          <span className={size === 'small' ? 'px-1' : 'px-2'}>{children}</span>
        )}
      </div>
    </button>
  )
}

Tab.Group = Group
export default Tab
